import { useState } from "react";
import { useSettings } from "@/hooks/useSettings";
import { useGameStats } from "@/hooks/useGameStats";

export default function SettingsSection() {
  const [showHelp, setShowHelp] = useState(false);
  const {
    difficulty,
    animationsEnabled,
    soundEnabled,
    setDifficulty,
    setAnimationsEnabled,
    setSoundEnabled,
  } = useSettings();
  const { resetStats } = useGameStats();

  const resetProgress = () => {
    resetStats();
    window.location.reload();
  };

  const difficulties = [
    {
      id: "easy" as const,
      label: "Facile",
      desc: "Heures rondes uniquement",
      color: "bg-green-500",
    },
    {
      id: "medium" as const,
      label: "Moyen",
      desc: "Quarts d'heure",
      color: "bg-orange-500",
    },
    {
      id: "hard" as const,
      label: "Difficile",
      desc: "Toutes les minutes",
      color: "bg-red-500",
    },
  ];

  return (
    <section
      id="parametres"
      className="relative min-h-[80vh] bg-brand-light flex items-center justify-center py-20"
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-2xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-brand-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-settings-3-line text-white text-2xl" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-text mb-4">
              Paramètres
            </h2>
            <p className="text-brand-muted text-lg">
              Personnalise ton expérience d'apprentissage
            </p>
          </div>

          {/* Settings card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Difficulty */}
            <div className="p-6 md:p-8 border-b border-gray-100">
              <h3 className="font-display text-lg font-semibold text-brand-text mb-4">
                <i className="ri-bar-chart-line mr-2 text-brand-accent" />
                Difficulté
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {difficulties.map((diff) => (
                  <button
                    key={diff.id}
                    onClick={() => setDifficulty(diff.id)}
                    className={`p-4 rounded-xl text-left transition-all cursor-pointer ${
                      difficulty === diff.id
                        ? "ring-2 ring-brand-accent bg-brand-accent/5"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-3 h-3 rounded-full ${diff.color}`}
                      />
                      <span className="font-semibold text-brand-text">
                        {diff.label}
                      </span>
                    </div>
                    <p className="text-sm text-brand-muted">{diff.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="p-6 md:p-8 border-b border-gray-100">
              <h3 className="font-display text-lg font-semibold text-brand-text mb-4">
                <i className="ri-equalizer-line mr-2 text-brand-accent" />
                Préférences
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <i className="ri-magic-line text-orange-500" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-text">Animations</p>
                      <p className="text-sm text-brand-muted">
                        Effets visuels et transitions
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAnimationsEnabled(!animationsEnabled)}
                    className={`relative w-14 h-8 rounded-full transition-all cursor-pointer ${
                      animationsEnabled ? "bg-brand-accent" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all ${
                        animationsEnabled ? "left-7" : "left-1"
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-volume-up-line text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-text">Sons</p>
                      <p className="text-sm text-brand-muted">
                        Effets sonores du jeu
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative w-14 h-8 rounded-full transition-all cursor-pointer ${
                      soundEnabled ? "bg-brand-accent" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all ${
                        soundEnabled ? "left-7" : "left-1"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 md:p-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowHelp(!showHelp)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-brand-text font-medium rounded-xl hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-question-line" />
                  Comment Jouer
                </button>
                <button
                  onClick={resetProgress}
                  className="flex-1 px-6 py-3 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-delete-bin-line" />
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>

          {/* Help modal */}
          {showHelp && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-xl font-bold text-brand-text">
                    Comment Jouer
                  </h3>
                  <button
                    onClick={() => setShowHelp(false)}
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-brand-text" />
                  </button>
                </div>

                <div className="space-y-4 text-brand-text">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-brand-accent" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">
                        1. Lis l'heure sur le bloc-notes
                      </p>
                      <p className="text-sm text-brand-muted">
                        L'heure à trouver s'affiche en haut de la carte de jeu.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-drag-move-line text-brand-accent" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">
                        2. Déplace les aiguilles
                      </p>
                      <p className="text-sm text-brand-muted">
                        Clique et glisse l'aiguille des heures et des minutes
                        pour régler l'heure.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-brand-accent" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">3. Vérifie ta réponse</p>
                      <p className="text-sm text-brand-muted">
                        Clique sur "As-tu trouvé ?" pour valider ton
                        résultat.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowHelp(false)}
                  className="w-full mt-6 px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-all cursor-pointer whitespace-nowrap"
                >
                  J'ai Compris !
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
